import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  clearHoverField,
  clearPlaceholderTarget,
  setHoverField,
  setPlaceholderTarget,
} from "../../../redux/features/queryBuilder/queryBuilderSlice";
import { getRightType } from "../../../utils/functions";
import { buildFieldPutData } from "../utils/conditionField";
import { ruleQueryKey } from "../hooks/queryKeys";
import {
  DRAG_ENTITY_FIELD,
  DRAG_ENTITY_SOURCE,
  DRAG_DATA_FIELD,
  DRAG_GROUP_BY,
  DRAG_ORDER_BY,
  DRAG_CONDITION_FIELD,
  DROP_CONDITION_GROUP,
  DROP_CONDITION_LEFT_FIELD,
  DROP_CONDITION_RIGHT_FIELD,
  DROP_CONDITION_SELECT,
  DROP_DATA_FIELDS_TARGET,
  DROP_ENTITIES_TARGET,
  DROP_GROUP_BY_TARGET,
  DROP_ORDER_BY_TARGET,
  DROP_ORDER_BY_SLOT,
} from "./kinds";
import {
  useAttachEntityMutation,
  useCreateFieldMutation,
  usePostConditionGroupMutation,
  usePostDataFieldMutation,
  usePostGroupByMutation,
  usePostOrderByMutation,
  useReorderDataFieldsMutation,
  useReorderOrderByMutation,
  useUpdateFieldMutation,
} from "../hooks/mutations";

/**
 * The QueryBuilder's central drag-and-drop coordinator.
 *
 * Every drop target inside the canvas registers itself with `useDroppable`
 * and tags its `data.current` with one of the DROP_* kinds. Every
 * draggable does the same with DRAG_*. This hook owns the single
 * `onDragEnd` callback, reads `(active.kind, over.kind)`, and fires the
 * right mutation.
 *
 * Hover state visualization is also centralized here: `onDragOver`
 * dispatches setHoverField / setPlaceholderTarget to the
 * queryBuilderSlice. Components read those selectors and apply visuals.
 */
export function useQueryBuilderDnd(ruleId) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const attachEntity = useAttachEntityMutation({ ruleId });
  const postDataField = usePostDataFieldMutation({ ruleId });
  const postGroupBy = usePostGroupByMutation({ ruleId });
  const postOrderBy = usePostOrderByMutation({ ruleId });
  const updateField = useUpdateFieldMutation({ ruleId });
  const createField = useCreateFieldMutation({ ruleId });
  const postConditionGroup = usePostConditionGroupMutation({ ruleId });
  const reorderDataFields = useReorderDataFieldsMutation({ ruleId });
  const reorderOrderBy = useReorderOrderByMutation({ ruleId });

  // Pointer sensor with a small activation distance so plain clicks don't
  // start drags. Keyboard sensor for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const postToSection = useCallback(
    (destKind, payload) => {
      if (destKind === DROP_DATA_FIELDS_TARGET)
        return postDataField.mutate(payload);
      if (destKind === DROP_GROUP_BY_TARGET) return postGroupBy.mutate(payload);
      if (destKind === DROP_ORDER_BY_TARGET) return postOrderBy.mutate(payload);
    },
    [postDataField, postGroupBy, postOrderBy],
  );

  const copyFieldAcrossSection = useCallback(
    ({ entityField, destQueryId, destKind }) => {
      const fieldId = entityField?.field?.id;
      const displayedEntityId = entityField?.displayedEntity?.id;
      if (!fieldId || !displayedEntityId) {
        toast.error("Couldn't resolve the dragged field.");
        return;
      }

      if (destKind === DROP_DATA_FIELDS_TARGET) {
        const rule = queryClient.getQueryData(ruleQueryKey(ruleId));
        if (
          dataFieldsAlreadyHasField(rule, destQueryId, fieldId, displayedEntityId)
        ) {
          toast.error("That field is already in Data Fields.");
          return;
        }
      }

      postToSection(destKind, {
        queryId: destQueryId,
        displayedEntityId,
        fieldId,
      });
    },
    [postToSection, queryClient, ruleId],
  );

  const dropFieldIntoCondition = useCallback(
    ({ fieldId, displayedEntityId, fieldType, target }) => {
      const type = getRightType(fieldType);

      if (target.kind === DROP_CONDITION_LEFT_FIELD) {
        const putData = {
          ...buildFieldPutData(target.fieldsData),
          leftFieldId: fieldId,
          leftDisplayedEntityId: displayedEntityId,
          type,
        };
        if (fieldType === "DATE") {
          putData.conditionInterval = "DAY_BEFORE_NOW";
        }
        updateField.mutate(putData);
        return;
      }

      if (target.kind === DROP_CONDITION_RIGHT_FIELD) {
        updateField.mutate({
          ...buildFieldPutData(target.fieldsData),
          rightFieldId: fieldId,
          rightDisplayedEntityId: displayedEntityId,
          type,
        });
        return;
      }

      if (target.kind === DROP_CONDITION_GROUP) {
        if (target.groupId) {
          // Append a new field to an existing group.
          createField.mutate({
            leftFieldId: fieldId,
            leftDisplayedEntityId: displayedEntityId,
            groupId: target.groupId,
            leftType: "FIELD",
            type,
          });
        } else {
          // No saved group yet → create the group then patch its
          // auto-created first field.
          postConditionGroup.mutate(
            {
              queryId: target.queryId,
              forJoin: target.forJoin,
            },
            {
              onSuccess: (resp) => {
                const group = resp?.data;
                const field = group?.fields?.[0];
                if (!field) return;
                const putData = {
                  id: field.id,
                  conditionOperatorType: field?.operator?.type,
                  conditionInterval: field?.rightPart?.conditionInterval,
                  type,
                  leftDisplayedEntityId: displayedEntityId,
                  leftFieldId: fieldId,
                };
                if (fieldType === "DATE") {
                  putData.conditionInterval = "DAY_BEFORE_NOW";
                }
                updateField.mutate(putData);
              },
            },
          );
        }
        return;
      }

      if (target.kind === DROP_CONDITION_SELECT) {
        // Nest a new group below the existing condition row. The new
        // group is seeded with the original left/right entity-field
        // refs and then we patch the auto-created first field with
        // the dropped entity-field on the left.
        const f = target.fieldsData;
        postConditionGroup.mutate(
          {
            groupOperator: "AND",
            queryId: Number(target.queryId),
            value: "",
            conditionGroupId: target.groupId,
            conditionOperatorType: "EQUALS",
            forJoin: target.forJoin,
            leftFieldId: f?.leftEntityField?.field?.id,
            leftDisplayedEntityId: f?.leftEntityField?.displayedEntity?.id,
            rightFieldId: f?.rightEntityField?.field?.id,
            rightDisplayedEntityId: f?.rightEntityField?.displayedEntity?.id,
          },
          {
            onSuccess: (resp) => {
              const created = resp?.data;
              const firstField = created?.fields?.[0];
              if (!firstField) return;
              updateField.mutate({
                id: firstField.id,
                leftFieldId: fieldId,
                leftDisplayedEntityId: displayedEntityId,
              });
            },
          },
        );
      }
    },
    [updateField, createField, postConditionGroup],
  );

  const onDragOver = useCallback(
    ({ active, over }) => {
      dispatch(clearHoverField());
      dispatch(clearPlaceholderTarget());

      const a = active?.data?.current;
      const o = over?.data?.current;
      if (!a || !o) return;

      if (a.kind !== DRAG_ENTITY_FIELD && !CHIP_HOME_KIND[a.kind]) return;

      if (o.kind === DROP_CONDITION_LEFT_FIELD) {
        dispatch(
          setHoverField({
            queryId: o.queryId,
            conditionGroupId: o.groupId,
            fieldId: o.fieldsData?.id,
            side: "left",
          }),
        );
      } else if (o.kind === DROP_CONDITION_RIGHT_FIELD) {
        dispatch(
          setHoverField({
            queryId: o.queryId,
            conditionGroupId: o.groupId,
            fieldId: o.fieldsData?.id,
            side: "right",
          }),
        );
      } else if (o.kind === DROP_CONDITION_GROUP) {
        dispatch(
          setPlaceholderTarget({
            queryId: o.queryId,
            conditionGroupId: o.groupId,
            type: o.forJoin ? "joinGroups" : "conditionGroups",
          }),
        );
      } else if (o.kind === DROP_CONDITION_SELECT) {
        dispatch(
          setPlaceholderTarget({
            queryId: o.queryId,
            conditionGroupId: o.groupId,
            type: o.forJoin ? "joinGroups" : "conditionGroups",
            isNestedGroup: true,
          }),
        );
      }
    },
    [dispatch],
  );

  const onDragCancel = useCallback(() => {
    dispatch(clearHoverField());
    dispatch(clearPlaceholderTarget());
  }, [dispatch]);

  const onDragEnd = useCallback(
    ({ active, over }) => {
      dispatch(clearHoverField());
      dispatch(clearPlaceholderTarget());

      const a = active?.data?.current;
      const o = over?.data?.current;
      if (!a || !o) return;

      // ── Entity catalog → entities target ─────────────────────────────────
      if (a.kind === DRAG_ENTITY_SOURCE && o.kind === DROP_ENTITIES_TARGET) {
        attachEntity.mutate({
          entityId: a.entity?.id,
          queryId: o.queryId,
        });
        return;
      }

      // ── Entity-field drops ───────────────────────────────────────────────
      if (a.kind === DRAG_ENTITY_FIELD) {
        const entityField = a.entityField;
        const fieldId = entityField?.id;
        const displayedEntityId = a.displayedEntityId;

        if (
          o.kind === DROP_DATA_FIELDS_TARGET ||
          o.kind === DROP_GROUP_BY_TARGET ||
          o.kind === DROP_ORDER_BY_TARGET
        ) {
          if (o.kind === DROP_DATA_FIELDS_TARGET) {
            const rule = queryClient.getQueryData(ruleQueryKey(ruleId));
            if (
              dataFieldsAlreadyHasField(rule, o.queryId, fieldId, displayedEntityId)
            ) {
              toast.error("That field is already in Data Fields.");
              return;
            }
          }
          postToSection(o.kind, {
            queryId: o.queryId,
            displayedEntityId,
            fieldId,
          });
          return;
        }

        dropFieldIntoCondition({
          fieldId,
          displayedEntityId,
          fieldType: entityField?.type,
          target: o,
        });
        return;
      }

      if (CHIP_HOME_KIND[a.kind]) {
        // Data Fields is a dnd-kit/sortable list: both the dragged chip and
        // the chip it's hovering over are tagged DRAG_DATA_FIELD (sortable
        // reuses one id/kind for both draggable and slot roles).
        if (
          a.kind === DRAG_DATA_FIELD &&
          o.kind === DRAG_DATA_FIELD &&
          a.queryId === o.queryId
        ) {
          // Look up the current dataFields list from the cached rule —
          // avoids stale snapshots in the drag data and makes the handler
          // tolerant of concurrent mutations.
          const rule = queryClient.getQueryData(ruleQueryKey(ruleId));
          const siblings = findListContainingId(
            rule,
            "dataFields",
            a.dataField?.id,
          );
          if (siblings) {
            const fromIndex = siblings.findIndex(
              (f) => f.id === a.dataField?.id,
            );
            const toIndex = siblings.findIndex((f) => f.id === o.dataField?.id);
            if (fromIndex >= 0 && toIndex >= 0) {
              reorderDataFields.mutate({
                fields: buildReorderPayload(siblings, fromIndex, toIndex),
              });
              return;
            }
          }
        }

        // A Data Field chip dropped anywhere else within its OWN section
        // (e.g. past the last chip, or in the gap between chips) is still a
        // reorder — move it to the end — never a new/duplicate placement.
        if (
          a.kind === DRAG_DATA_FIELD &&
          o.kind === DROP_DATA_FIELDS_TARGET &&
          a.queryId === o.queryId
        ) {
          const rule = queryClient.getQueryData(ruleQueryKey(ruleId));
          const siblings = findListContainingId(
            rule,
            "dataFields",
            a.dataField?.id,
          );
          const fromIndex = siblings?.findIndex(
            (f) => f.id === a.dataField?.id,
          );
          if (siblings && fromIndex >= 0 && fromIndex !== siblings.length - 1) {
            reorderDataFields.mutate({
              fields: buildReorderPayload(siblings, fromIndex, siblings.length - 1),
            });
          }
          return;
        }

        if (a.kind === DRAG_ORDER_BY && o.kind === DROP_ORDER_BY_SLOT) {
          const rule = queryClient.getQueryData(ruleQueryKey(ruleId));
          const siblings = findListContainingId(
            rule,
            "orderBy",
            a.orderByItem?.id,
          );
          if (siblings) {
            const fromIndex = siblings.findIndex(
              (f) => f.id === a.orderByItem?.id,
            );
            const toIndex = siblings.findIndex(
              (f) => f.id === o.orderByItem?.id,
            );
            if (fromIndex >= 0 && toIndex >= 0) {
              reorderOrderBy.mutate({
                fields: buildReorderPayload(siblings, fromIndex, toIndex),
              });
              return;
            }
          }
        }

        const targetSectionKind = SLOT_TO_SECTION_KIND[o.kind] ?? o.kind;
        if (
          targetSectionKind === DROP_DATA_FIELDS_TARGET ||
          targetSectionKind === DROP_GROUP_BY_TARGET ||
          targetSectionKind === DROP_ORDER_BY_TARGET
        ) {
          copyFieldAcrossSection({
            entityField: CHIP_ENTITY_FIELD[a.kind](a),
            destQueryId: o.queryId,
            destKind: targetSectionKind,
          });
          return;
        }

        if (
          o.kind === DROP_CONDITION_LEFT_FIELD ||
          o.kind === DROP_CONDITION_RIGHT_FIELD ||
          o.kind === DROP_CONDITION_GROUP ||
          o.kind === DROP_CONDITION_SELECT
        ) {
          const entityField = CHIP_ENTITY_FIELD[a.kind](a);
          const fieldId = entityField?.field?.id;
          const displayedEntityId = entityField?.displayedEntity?.id;
          if (!fieldId || !displayedEntityId) {
            toast.error("Couldn't resolve the dragged field.");
            return;
          }

          dropFieldIntoCondition({
            fieldId,
            displayedEntityId,
            fieldType: entityField?.field?.type,
            target: o,
          });
          return;
        }
      }
    },
    [
      attachEntity,
      postToSection,
      dropFieldIntoCondition,
      reorderDataFields,
      reorderOrderBy,
      copyFieldAcrossSection,
      dispatch,
      queryClient,
      ruleId,
    ],
  );

  // Sticky memory for the Data Fields "closest sibling" fallback below —
  // keyed by the active drag's id so it resets automatically between drags.
  const stickyDataFieldTarget = useRef({ activeId: null, targetId: null });

  /**
   * Collision strategy for the canvas. The pointer must actually be inside a
   * droppable for it to register — releasing an entity field anywhere outside
   * every box yields no target, so onDragEnd's `if (!over) return` guard
   * prevents accidental drops. (closestCenter, by contrast, always resolves to
   * the nearest box no matter how far away the pointer is.)
   *
   * pointerWithin also resolves nested droppables correctly — when the pointer
   * is over a small inner field inside a larger condition group, the inner one
   * sorts first by center distance and wins.
   *
   * rectIntersection is only a fallback for the keyboard sensor, which has no
   * pointer coordinates.
   *
   * For entity-field drags we drop the chip slots from the candidate set so the
   * Data Fields / Order By container wins (otherwise hovering a chip steals the
   * target, so the box never highlights or accepts). Slots stay active for chip
   * reordering.
   *
   * Reordering Data Fields is a special case: pointerWithin only resolves to a
   * sibling chip on a pixel-precise hit — everywhere else in the section it
   * resolves to the bare section box instead, so the sortable reflow/preview
   * only flickers on exact hits instead of tracking the whole drag gesture.
   * Once the pointer is anywhere inside the Data Fields box while dragging a
   * Data Field chip, we resolve to the CLOSEST sibling chip (by center
   * distance) instead, so the reflow and the reorder targeting stay live for
   * the whole gesture. pickStickyDataFieldTarget then damps that resolution
   * so it doesn't flip-flop (visible as the reflowing labels twitching)
   * between two nearly-equidistant siblings near their midpoint.
   */
  const collisionDetection = useCallback((args) => {
    let candidateArgs = args;
    if (args.active?.data?.current?.kind === DRAG_ENTITY_FIELD) {
      candidateArgs = {
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (c) =>
            c.data?.current?.kind !== DRAG_DATA_FIELD &&
            c.data?.current?.kind !== DROP_ORDER_BY_SLOT,
        ),
      };
    }

    const pointerCollisions = pointerWithin(candidateArgs);

    if (args.active?.data?.current?.kind === DRAG_DATA_FIELD) {
      if (stickyDataFieldTarget.current.activeId !== args.active.id) {
        stickyDataFieldTarget.current = {
          activeId: args.active.id,
          targetId: null,
        };
      }

      const hasChipHit = pointerCollisions.some(
        (c) => c.data?.droppableContainer?.data?.current?.kind === DRAG_DATA_FIELD,
      );
      const sectionHit = pointerCollisions.find(
        (c) =>
          c.data?.droppableContainer?.data?.current?.kind ===
          DROP_DATA_FIELDS_TARGET,
      );
      if (!hasChipHit && sectionHit) {
        const { queryId } = sectionHit.data.droppableContainer.data.current;
        const siblingChips = args.droppableContainers.filter(
          (c) =>
            c.data?.current?.kind === DRAG_DATA_FIELD &&
            c.data?.current?.queryId === queryId &&
            c.id !== args.active?.id,
        );
        if (siblingChips.length > 0) {
          const closest = closestCenter({
            ...args,
            droppableContainers: siblingChips,
          });
          if (closest.length > 0) {
            const resolved = pickStickyDataFieldTarget(
              closest,
              stickyDataFieldTarget.current.targetId,
            );
            stickyDataFieldTarget.current.targetId = resolved[0].id;
            return resolved;
          }
        }
      } else if (hasChipHit) {
        const chipHit = pointerCollisions.find(
          (c) => c.data?.droppableContainer?.data?.current?.kind === DRAG_DATA_FIELD,
        );
        stickyDataFieldTarget.current.targetId = chipHit?.id ?? null;
      }
    }

    if (pointerCollisions.length > 0) return pointerCollisions;
    if (args.pointerCoordinates) return []; // pointer drag released outside all boxes
    return rectIntersection(candidateArgs); // keyboard sensor (no pointer)
  }, []);

  return { sensors, collisionDetection, onDragOver, onDragEnd, onDragCancel };
}

// Membership set: which drag kinds are "chip-like" (already-placed items
// that can be copied into a section or a condition), keyed to where each
// one normally lives.
const CHIP_HOME_KIND = {
  [DRAG_DATA_FIELD]: DROP_DATA_FIELDS_TARGET,
  [DRAG_GROUP_BY]: DROP_GROUP_BY_TARGET,
  [DRAG_ORDER_BY]: DROP_ORDER_BY_TARGET,
  [DRAG_CONDITION_FIELD]: "condition-field",
};

const CHIP_ENTITY_FIELD = {
  [DRAG_DATA_FIELD]: (d) => d.dataField?.entityField,
  [DRAG_GROUP_BY]: (d) => d.groupItem?.entityField,
  [DRAG_ORDER_BY]: (d) => d.orderByItem?.entityField,
  [DRAG_CONDITION_FIELD]: (d) => d.entityField,
};

const SLOT_TO_SECTION_KIND = {
  // A Data Field chip's droppable role is tagged DRAG_DATA_FIELD (sortable),
  // not a separate DROP_* slot kind — map it to its section here so a
  // Group By/Order By/condition chip dropped ONTO a Data Field chip still
  // resolves as "this is the Data Fields section".
  [DRAG_DATA_FIELD]: DROP_DATA_FIELDS_TARGET,
  [DROP_ORDER_BY_SLOT]: DROP_ORDER_BY_TARGET,
};

// A sibling has to be genuinely closer than the current sticky target by
// this many px before collisionDetection switches to it — without this,
// hovering near the midpoint between two chips makes the "closest" one
// flip back and forth on every tiny pointer/measurement jitter, which
// reads as the reflowing labels twitching instead of settling.
const DATA_FIELD_HYSTERESIS_PX = 12;

/**
 * Keeps resolving to the previous sticky target unless a new candidate is
 * meaningfully closer (see DATA_FIELD_HYSTERESIS_PX). `closest` is a
 * closestCenter result, sorted ascending by distance.
 */
function pickStickyDataFieldTarget(closest, stickyId) {
  if (!stickyId) return closest;
  const sticky = closest.find((c) => c.id === stickyId);
  const nearest = closest[0];
  if (!sticky || nearest.id === stickyId) return closest;
  const stillCloseEnough =
    (sticky.data?.value ?? 0) - (nearest.data?.value ?? 0) < DATA_FIELD_HYSTERESIS_PX;
  return stillCloseEnough
    ? [sticky, ...closest.filter((c) => c.id !== stickyId)]
    : closest;
}

/**
 * Walks the rule's nested queries[] tree and returns the q[key] array
 * (e.g. "dataFields" or "orderBy") that contains an item with the given id.
 * Returns null if not found.
 */
function findListContainingId(rule, key, id) {
  if (!rule || id == null) return null;
  const visit = (queries) => {
    if (!queries?.length) return null;
    for (const q of queries) {
      if (q[key]?.some((f) => f.id === id)) {
        return q[key];
      }
      if (q.queries?.length) {
        const found = visit(q.queries);
        if (found) return found;
      }
    }
    return null;
  };
  return visit(rule.queries);
}

/**
 * Walks the rule's nested queries[] tree and returns the query node with
 * the given id. Returns null if not found.
 */
function findQueryById(rule, queryId) {
  if (!rule || queryId == null) return null;
  const visit = (queries) => {
    if (!queries?.length) return null;
    for (const q of queries) {
      if (q.id === queryId) return q;
      if (q.queries?.length) {
        const found = visit(q.queries);
        if (found) return found;
      }
    }
    return null;
  };
  return visit(rule.queries);
}

/**
 * Data Fields specifically must never end up with the same field twice —
 * a missed drop (or dragging a field that's already a Data Field in from
 * the entity accordion) should never silently duplicate it.
 */
function dataFieldsAlreadyHasField(rule, queryId, fieldId, displayedEntityId) {
  const query = findQueryById(rule, queryId);
  return !!query?.dataFields?.some(
    (item) =>
      item.entityField?.field?.id === fieldId &&
      item.entityField?.displayedEntity?.id === displayedEntityId,
  );
}

/**
 * Pure helper: build the `{ fields: [{id, rowNumber}] }` payload for
 * reorderDataFields based on the source list + move indices.
 */
function buildReorderPayload(list, fromIndex, toIndex) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const next = list.slice();
  if (
    fromIndex < 0 ||
    fromIndex >= next.length ||
    toIndex < 0 ||
    toIndex >= next.length
  ) {
    return list.map((f, i) => ({ id: f.id, rowNumber: i }));
  }
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next.map((f, i) => ({ id: f.id, rowNumber: i }));
}
